<template>
	<div class="card">
		<header class="card-header language-list">
			<h3 class="card-title">Content</h3>
			<lang-selector></lang-selector>
		</header>
		<div class="card-body">
			<div class="form-group">
				<label for="article-title">Title</label>
				<input @keypress="canEdit" @keyup="enableSaveDraft" class="input-large" id="article-title" name="article-title" type="text" v-model="editorContent.translations[clickedLang].title" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale">
				<p class="input-error" v-if="errors.errors[errorTitleCurrentLanguage]">{{ errors.errors[errorTitleCurrentLanguage] }}</p>
			</div>
			<div class="form-group">
				<label for="article-slug">URL Slug</label>
				<div class="input-group">
					<input @keypress="canEdit" @keyup="filterSlug" @change="enableSaveDraft" class="input-small" id="article-slug" name="article-slug" type="text" v-model="editorContent.translations[clickedLang].slug" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale">
					<a @click.prevent="refreshSlug(editorContent.translations[clickedLang].title)" class="btn btn-md btn-default btn-icon-only" href="#" title="Refresh slug"><i class="fas fa-sync-alt"></i></a>
				</div>
				<p class="input-error" v-if="errors.errors[errorSlugCurrentLanguage]">{{ errors.errors[errorSlugCurrentLanguage] }}</p>
			</div>
			<div class="form-group">
				<label class="flex">
					Article Content
					<span class="badge badge-success ml-auto" v-if="autoSave">Autosave In Progress</span>
				</label>
				<editor @onBlur="checkContent" id="article-content" :api-key="tinyMCEApiKey" v-model="editorContent.translations[clickedLang].content" :init="tinyMCEConfig" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale"/>
				<p class="input-error" v-if="errors.errors[errorContentCurrentLanguage]">{{ errors.errors[errorContentCurrentLanguage] }}</p>
			</div>

			<div class="form-group">
				<label for="custom-title">Custom Title</label>
				<input @keypress="canEdit" type="text" id="custom-title" name="custom-title" v-model="editorContent.translations[clickedLang].card_title" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale">
				<p class="input-error" v-if="errors.errors[errorContentCardTitle]">{{ errors.errors[errorContentCardTitle] }}</p>
			</div>
			<div class="form-group">
				<label for="custom-description">Custom Description</label>
				<textarea @keypress="canEdit" id="custom-description" name="custom-description" v-model="editorContent.translations[clickedLang].card_text" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale"></textarea>
				<p class="input-error" v-if="errors.errors[errorContentCardText]">{{ errors.errors[errorContentCardText] }}</p>
			</div>
			<div class="form-group">
				<label for="custom-code">Custom Code</label>
				<textarea @keypress="canEdit" id="custom-code" name="custom-code" v-model="editorContent.translations[clickedLang].card_code" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale"></textarea>
				<p class="input-error" v-if="errors.errors[errorContentCardCode]">{{ errors.errors[errorContentCardCode] }}</p>
			</div>
		</div>
	</div>
</template>

<script>
import {eventBus} from "../../app";
import Slug from "../../Utils/Slug";
import sw from "stopword";

export default {
	props: {
		editorContent: {
			required: true
		},
		errors: {}
	},
	data() {
		return {
			autoSave: false,
			userRoles: this.$currentUserRoles,
			defaultLocale: this.$defaultLanguage.locale,
			clickedLang: this.$defaultLanguage.locale,
			tinyMCEApiKey: this.$tinyMCEApiKey,
			tinyMCEConfig: this.$tinyMCEConfig
		}
	},
	created() {
		let vm = this;
		// When setActiveStatus method ran, change selected data in form fields
		/* From LangSelector */
		eventBus.$on('setActiveStatus', function (clickedLang) {
			vm.clickedLang = clickedLang;
		});

		vm.$watch('editorContent.translations.' + vm.clickedLang + '.content', _.debounce(function () {
			if (vm.editorContent.id) {
				eventBus.$emit('onArticleDraftSave');
				vm.autoSave = true;
			}
		}, 30000), {deep: true});

		eventBus.$on('onLoaderFinish', function () {
			vm.autoSave = false;
		});
		eventBus.$on('onArticleDraftSave', function () {
			vm.checkContent();
		});
		eventBus.$on('onArticlePublish', function () {
			vm.checkContent();
		});

		let shortcodeItems;
		if (vm.editorContent.categorySlug ==='comparison') {
			shortcodeItems = 'compare_providers blockquote blockquote-link speed-test';
		} else {
			shortcodeItems = 'blockquote blockquote-link speed-test';
		} // if

		let tinyMCEMenu = {
			menubar: 'file edit view insert format tools table help shortcodes',
			menu: {
				shortcodes: { title: 'Shortcodes', items: shortcodeItems }
			},
			style_formats: [
				{title: 'Headers', items: [
						{title: 'Header 1', format: 'h1'},
						{title: 'Header 2', format: 'h2'},
						{title: 'Header 3', format: 'h3'},
						{title: 'Header 4', format: 'h4'},
						{title: 'Header 5', format: 'h5'},
						{title: 'Header 6', format: 'h6'}
					]},
				{title: 'Inline', items: [
						{title: 'Bold', icon: 'bold', format: 'bold'},
						{title: 'Italic', icon: 'italic', format: 'italic'},
						{title: 'Underline', icon: 'underline', format: 'underline'},
						{title: 'Strikethrough', icon: 'strikethrough', format: 'strikethrough'},
						{title: 'Superscript', icon: 'superscript', format: 'superscript'},
						{title: 'Subscript', icon: 'subscript', format: 'subscript'},
						{title: 'Code', icon: 'code', format: 'code'}
					]},
				{title: 'Blocks', items: [
						{title: 'Paragraph', format: 'p'},
						{title: 'Div', format: 'div'},
						{title: 'Pre', format: 'pre'}
					]},
				{title: 'Alignment', items: [
						{title: 'Left', icon: 'alignleft', format: 'alignleft'},
						{title: 'Center', icon: 'aligncenter', format: 'aligncenter'},
						{title: 'Right', icon: 'alignright', format: 'alignright'},
						{title: 'Justify', icon: 'alignjustify', format: 'alignjustify'}
					]}
			],
			setup: function (editor) {
				if (vm.editorContent.categorySlug ==='comparison') {
					editor.ui.registry.addMenuItem('compare_providers', {
						text: 'Compare Providers',
						onAction: function () {
							window.open('/' + vm.$selectedSite.slug + '/providers/compare/article/' + vm.editorContent.id, '_blank').focus();
						}
					});
				} // if

				editor.ui.registry.addNestedMenuItem('blockquote', {
					text: 'Blockquotes',
					getSubmenuItems: function () {
						return [
							{
								type: 'menuitem',
								text: 'Blockquote without link',
								onAction: function () {
									editor.insertContent('[[post-object type="blockquote" author="AUTHOR HERE"]] QUOTE HERE [[/post-object]]');
								}
							},
							{
								type: 'menuitem',
								text: 'Blockquote with link',
								onAction: function () {
									editor.insertContent('[[post-object type="blockquote" author="AUTHOR HERE" author-link="LINK HERE"]] QUOTE HERE [[/post-object]]');
								}
							}
						];
					}
				});
			}
		}
		vm.tinyMCEConfig = Object.assign(tinyMCEMenu, vm.tinyMCEConfig);
	},
	methods: {
		refreshSlug: function (title) {
			let slug = new Slug();
			let slugTitle = sw.removeStopwords(title.split(' '), sw.selectedLanguage);
			if (this.clickedLang === 'en') {
				slugTitle = sw.removeStopwords(title.split(' '), words_en, sw.selectedLanguage);
			} // if
			this.editorContent.translations[this.clickedLang].slug = slug.refreshSlug(slugTitle);
		},
		enableSaveDraft: function () {
			eventBus.$emit('onEnableSaveDraft', false);
		},
		canEdit: function (event) {
			let vm = this;
			if (vm.userRoles.includes('users.translator') && vm.clickedLang === vm.defaultLocale) {
				event.preventDefault();
				event.stopPropagation();
			}
		},
		checkContent: function () {
			let replaceMe = {
				'“': '"',
				'”': '"',
				'&ldquo;': '"'
			};

			let vm = this;
			Object.keys(replaceMe).forEach(function (from) {
				let to = replaceMe[from];
				vm.editorContent.translations[vm.clickedLang].content = vm.editorContent.translations[vm.clickedLang].content.replaceAll(from, to);
			});

		},
		filterSlug: function() {
			let slug = this.editorContent.translations[this.clickedLang].slug;
			if (slug !== undefined || true) {
				this.editorContent.translations[this.clickedLang].slug = slug.toLowerCase();
			} // if
		}
	},
	computed: {
		errorTitleCurrentLanguage: function () {
			return 'articleEditorContent_translations_' + this.clickedLang + '_title';
		},
		errorSlugCurrentLanguage: function () {
			return 'articleEditorContent_translations_' + this.clickedLang + '_slug';
		},
		errorContentCurrentLanguage: function () {
			return 'articleEditorContent_translations_' + this.clickedLang + '_content';
		},
		errorContentCardTitle: function () {
			return 'articleEditorContent_translations_' + this.clickedLang + '_card_title';
		},
		errorContentCardText: function () {
			return 'articleEditorContent_translations_' + this.clickedLang + '_card_text';
		},
		errorContentCardCode: function () {
			return 'articleEditorContent_translations_' + this.clickedLang + '_card_code';
		}
	}
}
</script>

<style scoped>

</style>
